import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionSummary,
  Box,
  CircularProgress,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import React, { Suspense, useEffect, useRef, useState } from "react";
import {
  getVendorName,
  highlightText,
  shouldIncludeOrder,
} from "./orderSearchUtilities";
import {
  renderTrackingNumberIcon,
  renderTrackingNumbers,
  renderInvoiceNumbers,
} from "./TrackingNumberLogic";

const OrderTableLineItems = React.lazy(() => import("./OrderTableLineItems"));

const formatToCurrency = (value) => {
  const number = formatToNumber(value);
  if (isNaN(number)) {
    return "Invalid input";
  }
  return `$${number.toFixed(2)}`;
};

const formatToNumber = (value) => {
  if (typeof value !== "string") {
    return value;
  }
  return parseFloat(value.replace(/[$\s]/g, ""));
};

const LoadingFallback = () => (
  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
    <CircularProgress />
  </Box>
);

const OrderTablePO = ({ order, index, searchInput, numOfTableHeaders }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPoTruncated, setIsPoTruncated] = useState(false);
  const poRef = useRef(null);

  useEffect(() => {
    if (poRef.current) {
      setIsPoTruncated(poRef.current.scrollWidth > poRef.current.clientWidth);
    }
  }, [order.poNumber]);

  if (!shouldIncludeOrder(order, searchInput)) {
    return null;
  }

  const handleAccordionChange = (event, expanded) => {
    setIsExpanded(expanded);
  };

  return (
    <TableRow key={index}>
      <TableCell colSpan={numOfTableHeaders.length} padding="none">
        <Box
          sx={{
            "@media (max-width: 500px)": {
              fontSize: "14px", // Apply 14px font size globally for screen widths below 500px
            },
          }}
        >
          <Accordion
            key={index}
            sx={{
              boxShadow: "none", // Remove shadow on Accordion root
            }}
            onChange={handleAccordionChange}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`order-content-${index}`}
              id={`order-header-${index}`}
              sx={{
                width: "100%",
                padding: 0,
                paddingLeft: "1rem",
                flexShrink: 1,
                minWidth: 0,
                // Use more specific selectors
                "&.MuiAccordionSummary-root": {
                  minHeight: "48px",
                  height: "48px",
                  maxHeight: "48px",
                },
                "& .MuiAccordionSummary-content": {
                  margin: 0,
                  display: "flex",
                  flexWrap: "wrap",
                  overflow: "hidden",
                  flexShrink: 1,
                  minWidth: 0,
                  alignItems: "center",
                  "&.Mui-expanded": {
                    margin: 0, // Override expanded margin
                  },
                },
                transition: "background-color 0.1s", // Remove transition delay
                "&:hover": {
                  backgroundColor: "#e8e8e8",
                },
                "&.Mui-expanded": {
                  backgroundColor: "#e8e8e8",
                  ".MuiAccordionSummary-content": {
                    margin: 0,
                  },
                },
                "@media (max-width: 650px)": {
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    display: "none",
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  padding: 0,
                  "& .MuiAccordionSummary-content": {
                    margin: 0,
                  },
                  boxShadow: "none",
                  "@media (max-width: 500px)": {
                    justifyContent: "flex-end", // Right-justify all content below 500px
                  },
                }}
              >
                <Box
                  sx={{
                    width: "11%",
                    textAlign: "left",
                    minWidth: 0, // Allow the box to shrink below its content size
                    flexShrink: 1, // Allow shrinking when space is limited
                    userSelect: "text", // Make text selectable
                    whiteSpace: "nowrap", // Prevent text from wrapping
                    overflow: "hidden", // Hide overflowing text
                    textOverflow: "ellipsis", // Add ellipsis for overflow
                    "@media (max-width: 1000px)": {
                      width: "15%", // Increase width below 1000px
                    },
                    "@media (max-width: 750px)": {
                      width: "18%", // Increase width below 1000px
                    },
                    "@media (max-width: 500px)": {
                      width: "22%", // Increase width below 1000px
                      textAlign: "left", // Right-align text below 500px
                    },
                  }}
                >
                  {highlightText(order.OrderDate?.split("#")[0], searchInput)}
                </Box>
                <Box
                  sx={{
                    width: "13%",
                    textAlign: "left",
                    minWidth: 0,
                    flexShrink: 1,
                    userSelect: "text", // Make text selectable
                    "@media (max-width: 1000px)": {
                      width: "18%", // Increase width below 1000px
                    },
                    "@media (max-width: 750px)": {
                      width: "22%", // Increase width below 1000px
                    },
                    "@media (max-width: 500px)": {
                      width: "32%", // Increase width below 1000px
                      textAlign: "center", // Right-align text below 500px
                    },
                  }}
                >
                  {highlightText(
                    getVendorName(order.vendor) ||
                      order?.vendorName ||
                      order?.vendor,
                    searchInput,
                  )}
                </Box>
                <Tooltip
                  title={order.poNumber}
                  disableHoverListener={!isPoTruncated}
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        background: "#f9f9f9",
                        color: "#111",
                        fontSize: "14px", // Increased font size
                        padding: "8px 12px", // Increased padding
                        marginTop: "6px !important",
                        boxShadow:
                          "0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07)",
                        border: "1px solid darkgray",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#f9f9f9",
                        borderBottom: "none",
                        "::before": {
                          border: "1px solid darkgray",
                        },
                      },
                    },
                  }}
                >
                  <Box
                    ref={poRef}
                    sx={{
                      width: "11%",
                      textAlign: "left",
                      minWidth: 0,
                      flexShrink: 1,
                      userSelect: "text", // Make text selectable
                      whiteSpace: "nowrap", // Prevent text from wrapping
                      overflow: "hidden", // Hide overflowing text
                      textOverflow: "ellipsis", // Add ellipsis for overflow
                      "@media (max-width: 1000px)": {
                        width: "17%", // Increase width below 1000px
                      },
                      "@media (max-width: 750px)": {
                        width: "22%", // Increase width below 1000px
                      },
                      "@media (max-width: 500px)": {
                        width: "29%", // Increase width below 1000px
                        textAlign: "right", // Right-align text below 500px
                      },
                    }}
                  >
                    {highlightText(order.poNumber, searchInput)}
                  </Box>
                </Tooltip>
                <Box
                  sx={{
                    width: "14%",
                    textAlign: "left",
                    minWidth: 0,
                    flexShrink: 1,
                    userSelect: "text", // Make text selectable
                    "@media (max-width: 1000px)": {
                      display: "none", // Hide Order # below 1000px
                    },
                  }}
                >
                  {highlightText(order.orderNumber, searchInput)}
                </Box>
                <Box
                  sx={{
                    width: "15%",
                    textAlign: "left",
                    minWidth: 0,
                    flexShrink: 1,
                    userSelect: "text", // Make text selectable
                    "@media (max-width: 1000px)": {
                      display: "none", // Hide Invoice # below 1000px
                    },
                  }}
                >
                  {renderInvoiceNumbers(order.invoiceNumber, searchInput)}
                </Box>
                <Box
                  sx={{
                    width: { xs: "12%", md: "11%" },
                    textAlign: "left",
                    minWidth: 0,
                    flexShrink: 1,
                    userSelect: "text", // Make text selectable
                    "@media (max-width: 750px)": {
                      display: "none", // Hide orderItems field below 750px
                    },
                    "@media (max-width: 1000px)": {
                      width: "14%", // Increase width below 1000px
                    },
                  }}
                >
                  {order?.orderItems?.length}
                </Box>
                <Box
                  sx={{
                    width: { xs: "16%", md: "13%" },
                    textAlign: "left",
                    minWidth: 0,
                    flexShrink: 1,
                    userSelect: "text", // Make text selectable
                    "@media (max-width: 750px)": {
                      width: "20%", // Adjust width for icon
                    },
                    "@media (max-width: 1000px)": {
                      width: "16%", // Increase width below 1000px
                    },
                    "@media (max-width: 500px)": {
                      width: "20%", // Increase width below 500px
                      textAlign: "right", // Right-align tracking number and icon
                      paddingRight: "1rem", // Add padding to the right of the icon
                    },
                  }}
                >
                  {window.innerWidth <= 750
                    ? renderTrackingNumberIcon(order.trackingNumbers)
                    : renderTrackingNumbers(order.trackingNumbers, searchInput)}
                </Box>
                <Box
                  sx={{
                    width: { xs: "8%", md: "6%" },
                    textAlign: "right",
                    minWidth: 0,
                    flexShrink: 1,
                    userSelect: "text", // Make text selectable
                    "@media (max-width: 1400px)": {
                      width: { xs: "10%", md: "8%" },
                    },
                    "@media (max-width: 1000px)": {
                      width: "14%", // Increase width below 1000px
                    },
                    "@media (max-width: 500px)": {
                      display: "none", // Hide the Total field below 500px
                    },
                  }}
                >
                  {formatToCurrency(order?.total)}
                </Box>
              </Box>
            </AccordionSummary>
            <Suspense fallback={<LoadingFallback />}>
              <OrderTableLineItems
                order={order}
                searchInput={searchInput}
                vendor={order.vendor}
              />
            </Suspense>
          </Accordion>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default OrderTablePO;
